import React, { useState } from 'react';
import axios from 'axios';
import { Save, ArrowLeft } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const AddClient = () => {
  const { darkMode } = useSettings();
  const navigate = useNavigate();
  const [tipoPessoa, setTipoPessoa] = useState('PF');
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    // Dados básicos
    nome: '',
    cpf_cnpj: '',
    rg: '',
    rg_expedicao: '',
    tipo_documento: 'RG',
    data_nascimento: '',
    sexo: '',
    nome_pai: '',
    nome_mae: '',
    estado_civil: '',
    numero_dependentes: 0,
    email: '',
    telefone_principal: '',
    outros_telefones: '',
    endereco: '',
    cep: '',
    cidade_imovel: '',
    estado_imovel: '',
    
    // Cônjuge
    nome_conjuge: '',
    cpf_conjuge: '',
    data_nascimento_conjuge: '',
    
    // Certidões
    certidao_nascimento: 0,
    certidao_casamento: 0,
    
    // Documentos (caminhos)
    foto_documento_frente: '',
    foto_documento_verso: '',
    foto_selfie: '',
    foto_comprovante: '',
    foto_certidao_nascimento: '',
    foto_certidao_casamento: '',
    
    // Financeiro
    nivel_escolaridade: '',
    profissao: '',
    media_salarial: '',
    dia_pagamento: '',
    titulo_eleitor: '',
    senha_serasa: '',
    
    // Patrimônio
    tipo_imovel: '',
    complemento_imovel: '',
    tipo_imovel_detalhe: '',
    valor_imovel: '',
    possui_veiculo: 0,
    veiculo_valor: '',
    veiculo_ano: '',
    veiculo_placa: '',
    veiculo_placa_estado: '',
    
    // Empresa (PJ)
    empresa_endereco: '',
    empresa_bairro: '',
    empresa_cidade: '',
    empresa_estado: '',
    empresa_cep: '',
    empresa_telefone: '',
    empresa_email: '',
    empresa_funcionarios: '',
    empresa_faturamento: '',
    
    // Parceiro
    parceiro: 'Direto',
    
    // Tipo
    tipo_pessoa: 'PF'
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  const handleFileUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({
        ...prev,
        [fieldName]: response.data.filePath
      }));
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      alert('Erro ao fazer upload do arquivo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validações específicas
    if (tipoPessoa === 'PF') {
      // Se documento for CNH, data de expedição do RG é obrigatória
      if (formData.tipo_documento === 'CNH' && !formData.rg_expedicao) {
        alert('Se enviar CNH, a data de expedição do RG é obrigatória');
        setLoading(false);
        return;
      }

      // Certidão de nascimento não é mais obrigatória (opcional)
      // if (formData.estado_civil === 'Solteiro(a)' && !formData.certidao_nascimento) {
      //   alert('Se for solteiro, a certidão de nascimento é obrigatória');
      //   setLoading(false);
      //   return;
      // }

      // Se for casado ou união estável, certidão de casamento é obrigatória
      if ((formData.estado_civil === 'Casado(a)' || formData.estado_civil === 'União Estável') && !formData.certidao_casamento) {
        alert('Se for casado ou em união estável, a certidão de casamento é obrigatória');
        setLoading(false);
        return;
      }
    }

    try {
      const data = {
        ...formData,
        tipo_pessoa: tipoPessoa,
        origem_manual: 1,
        status: 'concluido',
        valor_total: tipoPessoa === 'PF' ? 997 : 1297,
        telefone: formData.telefone_principal
      };

      const response = await axios.post(`${API_URL}/leads`, data);
      
      // Criar checklist para o cliente
      await axios.post(`${API_URL}/rating-checklist`, {
        lead_id: response.data.id
      });

      alert('Cliente adicionado com sucesso!');
      navigate('/clients');
    } catch (error) {
      console.error('Erro ao adicionar cliente:', error);
      alert('Erro ao adicionar cliente');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/clients')}
            className={`p-2 rounded-lg ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'}`}
          >
            <ArrowLeft size={20} className={darkMode ? 'text-white' : 'text-gray-900'} />
          </button>
          <div>
            <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Adicionar Cliente Manualmente
            </h1>
            <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
              Insira os dados do cliente enviado por parceiro
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Tipo de Pessoa e Parceiro */}
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
          <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Tipo e Origem
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Tipo de Pessoa
              </label>
              <select
                name="tipo_pessoa"
                value={tipoPessoa}
                onChange={(e) => setTipoPessoa(e.target.value)}
                className={`w-full p-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              >
                <option value="PF">Pessoa Física</option>
                <option value="PJ">Pessoa Jurídica</option>
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Parceiro
              </label>
              <input
                type="text"
                name="parceiro"
                value={formData.parceiro}
                onChange={handleChange}
                placeholder="Nome do parceiro (ou 'Direto')"
                className={`w-full p-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
            </div>
          </div>
        </div>

        {/* Documentos */}
        {tipoPessoa === 'PF' && (
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
            <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              📎 Documentos e Comprovantes
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Documento Frente (RG/CNH) *
                </label>
                <input
                  type="file"
                  onChange={(e) => handleFileUpload(e, 'foto_documento_frente')}
                  accept="image/*,.pdf"
                  className={`w-full p-2 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
                {formData.foto_documento_frente && (
                  <p className={`text-xs mt-1 ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                    ✓ Arquivo enviado
                  </p>
                )}
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Documento Verso (RG/CNH) *
                </label>
                <input
                  type="file"
                  onChange={(e) => handleFileUpload(e, 'foto_documento_verso')}
                  accept="image/*,.pdf"
                  className={`w-full p-2 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
                {formData.foto_documento_verso && (
                  <p className={`text-xs mt-1 ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                    ✓ Arquivo enviado
                  </p>
                )}
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Self segurando o RG próximo do rosto *
                </label>
                <input
                  type="file"
                  onChange={(e) => handleFileUpload(e, 'foto_selfie')}
                  accept="image/*"
                  className={`w-full p-2 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
                {formData.foto_selfie && (
                  <p className={`text-xs mt-1 ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                    ✓ Arquivo enviado
                  </p>
                )}
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Comprovante de Endereço (com código de barras) *
                </label>
                <input
                  type="file"
                  onChange={(e) => handleFileUpload(e, 'foto_comprovante')}
                  accept="image/*,.pdf"
                  className={`w-full p-2 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
                {formData.foto_comprovante && (
                  <p className={`text-xs mt-1 ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                    ✓ Arquivo enviado
                  </p>
                )}
              </div>
              {formData.estado_civil === 'Solteiro(a)' && (
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Certidão de Nascimento (Opcional)
                  </label>
                  <input
                    type="file"
                    onChange={(e) => handleFileUpload(e, 'foto_certidao_nascimento')}
                    accept="image/*,.pdf"
                    className={`w-full p-2 rounded-lg border ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  />
                  {formData.foto_certidao_nascimento && (
                    <p className={`text-xs mt-1 ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                      ✓ Arquivo enviado
                    </p>
                  )}
                </div>
              )}
              {(formData.estado_civil === 'Casado(a)' || formData.estado_civil === 'União Estável') && (
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Certidão de Casamento *
                  </label>
                  <input
                    type="file"
                    onChange={(e) => handleFileUpload(e, 'foto_certidao_casamento')}
                    accept="image/*,.pdf"
                    className={`w-full p-2 rounded-lg border ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  />
                  {formData.foto_certidao_casamento && (
                    <p className={`text-xs mt-1 ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                      ✓ Arquivo enviado
                    </p>
                  )}
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Necessário que certidão de casamento esteja legível com todas as informações visíveis e completa.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Dados Pessoais */}
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
          <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Dados Pessoais
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Nome Completo *
              </label>
              <input
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                required
                className={`w-full p-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {tipoPessoa === 'PF' ? 'CPF *' : 'CNPJ *'}
              </label>
              <input
                type="text"
                name="cpf_cnpj"
                value={formData.cpf_cnpj}
                onChange={handleChange}
                required
                className={`w-full p-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                RG *
              </label>
              <input
                type="text"
                name="rg"
                value={formData.rg}
                onChange={handleChange}
                required
                className={`w-full p-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
            </div>
            {tipoPessoa === 'PF' && (
              <>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Tipo de Documento
                  </label>
                  <select
                    name="tipo_documento"
                    value={formData.tipo_documento}
                    onChange={handleChange}
                    className={`w-full p-2 rounded-lg border ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="RG">RG / Carteira de Identidade</option>
                    <option value="CNH">CNH (Carteira Nacional de Habilitação)</option>
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Data de Expedição do RG {formData.tipo_documento === 'CNH' && '*'}
                  </label>
                  <input
                    type="date"
                    name="rg_expedicao"
                    value={formData.rg_expedicao}
                    onChange={handleChange}
                    required={formData.tipo_documento === 'CNH'}
                    className={`w-full p-2 rounded-lg border ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  />
                  {formData.tipo_documento === 'CNH' && (
                    <p className={`text-xs mt-1 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                      Obrigatório se usar CNH
                    </p>
                  )}
                </div>
              </>
            )}
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Data de Nascimento
              </label>
              <input
                type="date"
                name="data_nascimento"
                value={formData.data_nascimento}
                onChange={handleChange}
                className={`w-full p-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Sexo
              </label>
              <select
                name="sexo"
                value={formData.sexo}
                onChange={handleChange}
                className={`w-full p-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              >
                <option value="">Selecione</option>
                <option value="Masculino">Masculino</option>
                <option value="Feminino">Feminino</option>
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Estado Civil
              </label>
              <select
                name="estado_civil"
                value={formData.estado_civil}
                onChange={handleChange}
                className={`w-full p-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              >
                <option value="">Selecione</option>
                <option value="Solteiro(a)">Solteiro(a)</option>
                <option value="Casado(a)">Casado(a)</option>
                <option value="Divorciado(a)">Divorciado(a)</option>
                <option value="Viúvo(a)">Viúvo(a)</option>
                <option value="Separado(a) Judicialmente">Separado(a) Judicialmente</option>
                <option value="União Estável">União Estável</option>
              </select>
            </div>
            {tipoPessoa === 'PF' && (
              <>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Nº de Dependentes (filhos)
                  </label>
                  <input
                    type="number"
                    name="numero_dependentes"
                    value={formData.numero_dependentes}
                    onChange={handleChange}
                    min="0"
                    className={`w-full p-2 rounded-lg border ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
                {formData.estado_civil === 'Solteiro(a)' && (
                  <div className="flex items-center gap-2 mt-6">
                    <input
                      type="checkbox"
                      name="certidao_nascimento"
                      checked={formData.certidao_nascimento === 1}
                      onChange={(e) => setFormData(prev => ({ ...prev, certidao_nascimento: e.target.checked ? 1 : 0 }))}
                      className="w-5 h-5"
                    />
                    <label className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Certidão de Nascimento enviada (Opcional)
                    </label>
                    <p className={`text-xs ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                      - Se o cliente não possuir, o serviço pode ser finalizado normalmente
                    </p>
                  </div>
                )}
                {(formData.estado_civil === 'Casado(a)' || formData.estado_civil === 'União Estável') && (
                  <div className="flex items-center gap-2 mt-6">
                    <input
                      type="checkbox"
                      name="certidao_casamento"
                      checked={formData.certidao_casamento === 1}
                      onChange={(e) => setFormData(prev => ({ ...prev, certidao_casamento: e.target.checked ? 1 : 0 }))}
                      className="w-5 h-5"
                    />
                    <label className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Certidão de Casamento enviada *
                    </label>
                  </div>
                )}
              </>
            )}
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Nome do Pai
              </label>
              <input
                type="text"
                name="nome_pai"
                value={formData.nome_pai}
                onChange={handleChange}
                className={`w-full p-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Nome da Mãe
              </label>
              <input
                type="text"
                name="nome_mae"
                value={formData.nome_mae}
                onChange={handleChange}
                className={`w-full p-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
            </div>
          </div>
        </div>

        {/* Dados do Cônjuge */}
        {(formData.estado_civil === 'Casado(a)' || formData.estado_civil === 'União Estável') && (
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
            <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Dados do Cônjuge
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Nome do Cônjuge *
                </label>
                <input
                  type="text"
                  name="nome_conjuge"
                  value={formData.nome_conjuge}
                  onChange={handleChange}
                  required
                  className={`w-full p-2 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  CPF do Cônjuge *
                </label>
                <input
                  type="text"
                  name="cpf_conjuge"
                  value={formData.cpf_conjuge}
                  onChange={handleChange}
                  required
                  className={`w-full p-2 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Data de Nascimento do Cônjuge *
                </label>
                <input
                  type="date"
                  name="data_nascimento_conjuge"
                  value={formData.data_nascimento_conjuge}
                  onChange={handleChange}
                  required
                  className={`w-full p-2 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>
            </div>
          </div>
        )}

        {/* Contato e Endereço */}
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
          <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Contato e Endereço
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                E-mail
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full p-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Telefone Principal *
              </label>
              <input
                type="text"
                name="telefone_principal"
                value={formData.telefone_principal}
                onChange={handleChange}
                required
                className={`w-full p-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Outros Telefones
              </label>
              <input
                type="text"
                name="outros_telefones"
                value={formData.outros_telefones}
                onChange={handleChange}
                className={`w-full p-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Endereço
              </label>
              <input
                type="text"
                name="endereco"
                value={formData.endereco}
                onChange={handleChange}
                className={`w-full p-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                CEP
              </label>
              <input
                type="text"
                name="cep"
                value={formData.cep}
                onChange={handleChange}
                className={`w-full p-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Cidade
              </label>
              <input
                type="text"
                name="cidade_imovel"
                value={formData.cidade_imovel}
                onChange={handleChange}
                className={`w-full p-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Estado
              </label>
              <input
                type="text"
                name="estado_imovel"
                value={formData.estado_imovel}
                onChange={handleChange}
                className={`w-full p-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
            </div>
          </div>
        </div>

        {/* Dados Financeiros */}
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
          <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Dados Financeiros
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Nível de Escolaridade
              </label>
              <select
                name="nivel_escolaridade"
                value={formData.nivel_escolaridade}
                onChange={handleChange}
                className={`w-full p-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              >
                <option value="">Selecione</option>
                <option value="Fundamental">Fundamental</option>
                <option value="Médio">Médio</option>
                <option value="Superior">Superior</option>
                <option value="Pós-graduação">Pós-graduação</option>
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Profissão
              </label>
              <input
                type="text"
                name="profissao"
                value={formData.profissao}
                onChange={handleChange}
                className={`w-full p-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Média Salarial
              </label>
              <input
                type="number"
                name="media_salarial"
                value={formData.media_salarial}
                onChange={handleChange}
                className={`w-full p-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Dia do Pagamento
              </label>
              <input
                type="number"
                name="dia_pagamento"
                value={formData.dia_pagamento}
                onChange={handleChange}
                min="1"
                max="31"
                className={`w-full p-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Título de Eleitor
              </label>
              <input
                type="text"
                name="titulo_eleitor"
                value={formData.titulo_eleitor}
                onChange={handleChange}
                className={`w-full p-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Senha SERASA
              </label>
              <input
                type="text"
                name="senha_serasa"
                value={formData.senha_serasa}
                onChange={handleChange}
                className={`w-full p-2 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
            </div>
          </div>
        </div>

        {/* Empresa (PJ) */}
        {tipoPessoa === 'PJ' && (
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
            <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Dados da Empresa
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Endereço da Empresa
                </label>
                <input
                  type="text"
                  name="empresa_endereco"
                  value={formData.empresa_endereco}
                  onChange={handleChange}
                  className={`w-full p-2 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Bairro
                </label>
                <input
                  type="text"
                  name="empresa_bairro"
                  value={formData.empresa_bairro}
                  onChange={handleChange}
                  className={`w-full p-2 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Cidade
                </label>
                <input
                  type="text"
                  name="empresa_cidade"
                  value={formData.empresa_cidade}
                  onChange={handleChange}
                  className={`w-full p-2 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Estado
                </label>
                <input
                  type="text"
                  name="empresa_estado"
                  value={formData.empresa_estado}
                  onChange={handleChange}
                  className={`w-full p-2 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  CEP
                </label>
                <input
                  type="text"
                  name="empresa_cep"
                  value={formData.empresa_cep}
                  onChange={handleChange}
                  className={`w-full p-2 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Telefone
                </label>
                <input
                  type="text"
                  name="empresa_telefone"
                  value={formData.empresa_telefone}
                  onChange={handleChange}
                  className={`w-full p-2 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  E-mail
                </label>
                <input
                  type="email"
                  name="empresa_email"
                  value={formData.empresa_email}
                  onChange={handleChange}
                  className={`w-full p-2 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Número de Funcionários
                </label>
                <input
                  type="number"
                  name="empresa_funcionarios"
                  value={formData.empresa_funcionarios}
                  onChange={handleChange}
                  className={`w-full p-2 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Faturamento
                </label>
                <input
                  type="text"
                  name="empresa_faturamento"
                  value={formData.empresa_faturamento}
                  onChange={handleChange}
                  className={`w-full p-2 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            <Save size={20} />
            {loading ? 'Salvando...' : 'Salvar Cliente'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddClient;
